import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CustomAxios from "../utils/CustomAxios";
import { Toast } from "primereact/toast";

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return num.toFixed(2);
};

export function PosBilling() {
  // Get user and inventory from Redux
  const user = useSelector((state) => state.user.id);
  const selectedHub = useSelector((state) => state.user.selected_hub);
  
  // Normalize inventory_id - handle array, string, or number
  const normalizeInventoryId = (value) => {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return normalizeInventoryId(value[0]);
    }
    if (typeof value === 'string') {
      if (value.trim() === '' || value === 'null') return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }
    return value;
  };
  
  const inventoryId = normalizeInventoryId(selectedHub) || 
  (() => {
      // Fallback to localStorage if Redux value is not available
      const storedSelectedHub = localStorage.getItem('selected_hub');
      if (storedSelectedHub) {
        try {
          const parsed = JSON.parse(storedSelectedHub);
          return normalizeInventoryId(parsed);
        } catch (error) {
          return normalizeInventoryId(storedSelectedHub);
        }
      }
      return null;
    })();
  
  const [activeBill, setActiveBill] = useState(0);
  const [searchProduct, setSearchProduct] = useState("");
  const [searchInvoice, setSearchInvoice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [cashAmount, setCashAmount] = useState("");
  const [invoiceId, setInvoiceId] = useState("XXXXXXX");
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  const [invoiceDate] = useState(getCurrentDate());
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const toast = useRef(null);

  const [bills, setBills] = useState([
    {
      id: 1,
      name: "Bill 1",
      products: [],
      customerName: "",
      mobileNumber: "",
      mobileNumberError: "",
      invoiceId: "XXXXXXX",
    },
  ]);
  
  const pageRef = useRef(null);

  const focusNextInput = (direction = "next") => {
    if (!pageRef.current) return;
  
    const inputs = Array.from(
      pageRef.current.querySelectorAll(
        'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])'
      )
    ).filter((el) => el.offsetParent !== null); // only visible
  
    if (inputs.length === 0) return;
  
    const active = document.activeElement;
    const currentIndex = inputs.indexOf(active);
  
    let nextIndex;
    if (direction === "next") {
      nextIndex = currentIndex >= 0 ? (currentIndex + 1) % inputs.length : 0;
    } else {
      nextIndex =
      currentIndex >= 0
      ? (currentIndex - 1 + inputs.length) % inputs.length
      : inputs.length - 1;
    }
  
    inputs[nextIndex].focus();
  };
  
  const handleKeyDownOnlyInputs = (e) => {
    if (e.key !== "Tab") return;
  
    // If user is inside input, keep our controlled tab order
    e.preventDefault();
    focusNextInput(e.shiftKey ? "prev" : "next");
  };
  
  const products = bills[activeBill]?.products || [];
  const activeCustomerName = bills[activeBill]?.customerName || "";
  const activeMobile = bills[activeBill]?.mobileNumber || "";
  const activeMobileError = bills[activeBill]?.mobileNumberError || "";


  const extractCapacityFromProduct = (productData) => {
    if (!productData) return "";
    
    if (productData.capacitydetails) {
      const capacityDetails = productData.capacitydetails;
      if (typeof capacityDetails === 'object' && !Array.isArray(capacityDetails)) {
        const invIdStr = String(inventoryId);
        const invIdNum = inventoryId;
        
        let cap = capacityDetails[invIdStr]?.capacities?.[0];
        
        if (!cap && capacityDetails[invIdNum]) {
          cap = capacityDetails[invIdNum]?.capacities?.[0];
        }
        
        if (!cap) {
          const firstKey = Object.keys(capacityDetails)[0];
          cap = capacityDetails[firstKey]?.capacities?.[0];
        }
        
        if (cap) {
          return cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
        }
      } else if (Array.isArray(capacityDetails) && capacityDetails.length > 0) {
        const cap = capacityDetails[0];
        return cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
      }
    }
    
    if (productData.item_capacity) {
      return productData.item_capacity;
    }
    
    return "";
  };


  const syncQuantityWithBackend = async (productsToSync = null) => {
    if (!user || !inventoryId) {
      console.warn("User or inventory ID not available", { user, inventoryId });
      return;
    }

    try {
      // Use provided products or get from current state
      const activeBillProducts = productsToSync || bills[activeBill]?.products || [];
      const formattedProducts = activeBillProducts.map((product) => ({
        product_id: product.productId || product.id,
        quantity: product.quantity,
      }));

      const payload = {
        products: formattedProducts,
        inventory_id: inventoryId,
        user: user,
      };

      const response = await CustomAxios.post("kiosk_order_creation/", payload);
      
      // Update products with capacity information from backend response
      if (response?.data?.product_details) {
        console.log("Product details from backend:", response.data.product_details);
        
        const productDetailsMap = {};
        response.data.product_details.forEach((pd) => {
          productDetailsMap[pd.id] = pd;
        });

        setBills((prev) =>
          prev.map((bill, index) => {
            if (index === activeBill) {
              return {
                ...bill,
                products: bill.products.map((product) => {
                  const productDetail = productDetailsMap[product.productId || product.id];
                  if (productDetail) {
                    // Preserve the originally selected capacity if it exists
                    let selectedCap = null;
                    let capacity = product.capacity || "-"; // Preserve original capacity
                    let itemOffersPrice = product.itemOffersPrice || product.price;
                    let itemTax = product.itemTax || product.gst || 0;
                    if (productDetail.capacitydetails) {
                      const capacityDetails = productDetail.capacitydetails;
                      let allCapacities = [];
                      
                      // Collect all capacities from the response
                      if (typeof capacityDetails === 'object' && !Array.isArray(capacityDetails)) {
                        Object.keys(capacityDetails).forEach((invKey) => {
                          const invData = capacityDetails[invKey];
                          if (invData?.capacities && Array.isArray(invData.capacities)) {
                            allCapacities = allCapacities.concat(invData.capacities);
                          } else if (Array.isArray(invData)) {
                            allCapacities = allCapacities.concat(invData);
                          }
                        });
                      } else if (Array.isArray(capacityDetails)) {
                        allCapacities = capacityDetails;
                      }
                      
                      console.log("Capacity details:", allCapacities.map(cap => ({
                        id: cap.id,
                        item_capacity: cap.item_capacity,
                        capacityValue: cap.capacityValue,
                        unit: cap.unit
                      })));
                      
                      if (product.selectedCapacityId && allCapacities.length > 0) {
                        selectedCap = allCapacities.find(cap => cap.id === product.selectedCapacityId);
                      }
                      
                      if (!selectedCap && product.selectedCapacityInfo && allCapacities.length > 0) {
                        selectedCap = allCapacities.find(cap => {
                          const capInfo = cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
                          return capInfo === product.selectedCapacityInfo;
                        });
                      }
                      
                      if (!selectedCap && allCapacities.length > 0) {
                        selectedCap = allCapacities[0];
                      }
                      
                      if (selectedCap) {
                        itemOffersPrice = selectedCap.item_offers_price || selectedCap.item_selling_price || selectedCap.item_price || itemOffersPrice;
                        itemTax = selectedCap.item_tax || itemTax;
                        capacity = selectedCap.item_capacity || `${selectedCap.capacityValue || ''} ${selectedCap.unit || ''}`.trim() || capacity;
                        // console.log("Using selected capacity:", capacity, "Price:", itemOffersPrice);
                      } else {
                        // console.log("No selected capacity found, using fallback logic");
                        const invIdStr = String(inventoryId);
                        const invIdNum = inventoryId;
                        let cap = capacityDetails[invIdStr]?.capacities?.[0];
                        if (!cap && capacityDetails[invIdNum]) {
                          cap = capacityDetails[invIdNum]?.capacities?.[0];
                        }
                        if (!cap && typeof capacityDetails === 'object' && !Array.isArray(capacityDetails)) {
                          const firstKey = Object.keys(capacityDetails)[0];
                          cap = capacityDetails[firstKey]?.capacities?.[0];
                        }
                        if (cap) {
                          itemOffersPrice = cap.item_offers_price || cap.item_selling_price || cap.item_price || itemOffersPrice;
                          itemTax = cap.item_tax || itemTax;
                          capacity = cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim() || capacity;
                        }
                      }
                    }
                    
                    const updatedProduct = {
                      ...product,
                      itemOffersPrice: itemOffersPrice,
                      itemTax: itemTax,
                      price: itemOffersPrice, // Update price to item_offers_price
                      gst: itemTax, // Update gst to item_tax
                      total: (itemOffersPrice * product.quantity) + ((itemOffersPrice * itemTax / 100) * product.quantity), // Recalculate total
                      capacity: capacity, // Preserve or update capacity
                    };
                    
                    return updatedProduct;
        }
        return product;
                }),
              };
            }
            return bill;
          })
        );
        console.log("=== END SYNC RESPONSE DEBUG ===");
      }
    } catch (error) {
      console.error("Error syncing quantity with backend:", error);
    }
  };

  const handleQuantityChange = (id, change) => {
    const product = products.find((p) => p.id === id);
    if (!product) return;

    const newQuantity = Math.max(1, product.quantity + change);
    const itemOffersPrice = product.itemOffersPrice || product.price;
    const taxPercentage = product.itemTax || product.gst || 0; // This is a percentage
    const subtotal = itemOffersPrice * newQuantity;
    // Calculate tax amount from percentage
    const taxTotal = (itemOffersPrice * taxPercentage / 100) * newQuantity;
    const newTotalRaw = subtotal + taxTotal;
    const newTotal = parseFloat(newTotalRaw.toFixed(2));

    setBills((prev) => {
      const updatedBills = prev.map((bill, index) => {
        if (index === activeBill) {
          const updatedProducts = bill.products.map((p) =>
            p.id === id ? { ...p, quantity: newQuantity, total: newTotal } : p
          );
          return {
            ...bill,
            products: updatedProducts,
          };
        }
        return bill;
      });
      
      // Sync with backend using updated products
      const activeBillProducts = updatedBills[activeBill]?.products || [];
      syncQuantityWithBackend(activeBillProducts);
      
      return updatedBills;
    });
  };

  const handleRemoveProduct = (id) => {
    setBills((prev) => {
      const updatedBills = prev.map((bill, index) => {
        if (index === activeBill) {
          const updatedProducts = bill.products.filter(
            (product) => product.id !== id
          );
  
          return {
            ...bill,
            products: updatedProducts,
            customerName: updatedProducts.length === 0 ? "" : bill.customerName,
            mobileNumber: updatedProducts.length === 0 ? "" : bill.mobileNumber,
            mobileNumberError: updatedProducts.length === 0 ? "" : bill.mobileNumberError,
          };
        }
        return bill;
      });
  
      const activeBillProducts = updatedBills[activeBill]?.products || [];
      syncQuantityWithBackend(activeBillProducts);
  
      if (activeBillProducts.length === 0) {
        setInvoiceId("XXXXXXX");
      }
  
      return updatedBills;
    });
  };
  
  
  const handleNewBill = () => {
    const newBillNumber = bills.length + 1;
    const newBill = {
      id: newBillNumber,
      name: `Bill ${newBillNumber}`,
      products: [],
      customerName: "",
      mobileNumber: "",
      mobileNumberError: "",
    };    
    setBills((prev) => [...prev, newBill]);
    setActiveBill(bills.length); // Switch to the new bill
    setInvoiceId("XXXXXXX"); // Reset invoice ID for new bill
  };

  const handleClearBill = () => {
    setBills((prev) =>
      prev.map((bill, index) => {
        if (index === activeBill) {
          return {
            ...bill,
            products: [],
            customerName: "",
            mobileNumber: "",
            mobileNumberError: "",
          };
        }
        return bill;
      })
    );
  
    setInvoiceId("XXXXXXX");
  };
  

  const handleCloseBill = (event, billIndex) => {
    event.stopPropagation(); // Prevent tab change when clicking close
    
    if (bills.length <= 1) {
      // Don't allow closing the last bill
      return;
    }

    setBills((prev) => {
      const newBills = prev.filter((_, index) => index !== billIndex);
      return newBills;
    });

    // Adjust activeBill index
    if (billIndex === activeBill) {
      // If closing the active bill, switch to the previous one or first one
      if (billIndex > 0) {
        setActiveBill(billIndex - 1);
      } else {
        setActiveBill(0);
      }
    } else if (billIndex < activeBill) {
      // If closing a bill before the active one, decrement activeBill
      setActiveBill(activeBill - 1);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      setHighlightedIndex(-1);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await CustomAxios.get(
      `products-search/${encodeURIComponent(query)}/`,
      {
        params: {
          inventory_id: inventoryId,
        },
      }
    );

      // Response structure: array of product objects with item_name, sku_code, etc.
      let products = [];
      
      if (Array.isArray(response?.data)) {
        products = response.data;
      } else if (response?.data?.product_id) {
        // If response has product_id array, map it with other data
        const productIds = response.data.product_id || [];
        const inventoryIds = response.data.inventory_id || [];
        const offers = response.data.offer || [];
        
        // Try to get product details from the response data array
        const productData = Array.isArray(response.data) ? response.data : [];
        
        products = productIds.map((productId, index) => {
          // Find matching product data by ID
          const productDetail = productData.find(p => p.id === productId) || {};
          return {
            id: productId,
            inventory_id: inventoryIds[index],
            offer: offers[index],
            ...productDetail
          };
        });
      }
      
      
      if (products.length > 0 && user && inventoryId) {
        try {
          // console.log("-----products---------",products)
          const formattedProductsForDetails = products.map((product) => ({
            product_id: product.id,
            quantity: 1, // Use quantity 1 just to get product details
          }));

          const detailsPayload = {
            products: formattedProductsForDetails,
            inventory_id: inventoryId,
            user: user,
          };

          const detailsResponse = await CustomAxios.post("kiosk_order_creation/", detailsPayload);
          console.log("-----------",detailsResponse?.data?.product_details)
          if (detailsResponse?.data?.product_details) {
            
            // Create a map of product details by product ID
            const productDetailsMap = {};
            detailsResponse.data.product_details.forEach((pd) => {
              productDetailsMap[pd.id] = pd;
            });
            
            // Merge capacity details into products
            products = products.map((product) => {
              const fullProductDetails = productDetailsMap[product.id];
              if (fullProductDetails) {
                return {
                  ...product,
                  capacitydetails: fullProductDetails.capacitydetails || product.capacitydetails,
                  // Include other relevant fields from full details
                  ...fullProductDetails
                };
              }
              return product;
            });
            
          } else {
            console.log("No product details received from backend");
          }
        } catch (detailsError) {
          console.error("Error fetching product details:", detailsError);
          // Continue with original products even if details fetch fails
        }
      }
      
      // Expand products with multiple capacities into separate entries
      const expandedProducts = [];
      products.forEach((product, productIndex) => {
        console.log(`\n--- Product ${productIndex + 1}: ${product.item_name || product.name || 'Unknown'} (ID: ${product.id}) ---`);
        
        const capacityDetails = product.capacitydetails;
        
        let capacities = [];
        // console.log("------capp--------",capacityDetails)
        if (capacityDetails) {
          if (typeof capacityDetails === 'object' && !Array.isArray(capacityDetails)) {
            
            Object.keys(capacityDetails).forEach((invKey) => {
              const invData = capacityDetails[invKey];
              
              let invCapacities = [];
              if (invData?.capacities && Array.isArray(invData.capacities)) {
                invCapacities = invData.capacities;
              } else if (Array.isArray(invData)) {
                invCapacities = invData;
              } else if (invData && typeof invData === 'object') {
                // Check if it's a nested structure
                if (invData.capacities && Array.isArray(invData.capacities)) {
                  invCapacities = invData.capacities;
                }
              }
              
              capacities = capacities.concat(invCapacities);
            });
          } else if (Array.isArray(capacityDetails)) {
            capacities = capacityDetails;
          }
        } 
        
        if (capacities.length > 0) {
          console.log("Capacity details:", capacities.map((cap, idx) => ({
            index: idx,
            id: cap.id,
            item_capacity: cap.item_capacity,
            capacityValue: cap.capacityValue,
            unit: cap.unit,
            item_offers_price: cap.item_offers_price
          })));
        }
        
        // If product has multiple capacities, create separate entries for each
        if (capacities.length > 0) {
          capacities.forEach((capacity, capIndex) => {
            const capacityInfo = capacity.item_capacity || 
              `${capacity.capacityValue || ''} ${capacity.unit || ''}`.trim() || 
              'Standard';
            
            
            expandedProducts.push({
              ...product,
              // Create unique ID for each capacity variation
              searchId: `${product.id}_${capacity.id || capIndex}`,
              selectedCapacity: capacity,
              capacityInfo: capacityInfo,
              // Store all capacities for reference
              allCapacities: capacities
            });
          });
        } else {
          // Product has no capacity details, add as single entry
          expandedProducts.push({
            ...product,
            searchId: `${product.id}_default`,
            selectedCapacity: null,
            capacityInfo: product.item_capacity || '-',
            allCapacities: []
          });
        }
      });
      
      console.log("Expanded products summary:", expandedProducts.map(p => ({
        name: p.item_name || p.name,
        searchId: p.searchId,
        capacityInfo: p.capacityInfo
      })));
      
      setSearchResults(expandedProducts);
      setShowSearchResults(true);
      setHighlightedIndex(-1); 
    } catch (error) {
      console.error("Error searching products:", error);
      setSearchResults([]);
      setShowSearchResults(false);
      setHighlightedIndex(-1);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchProduct(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    if (value.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(value);
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setHighlightedIndex(-1);
    }
  };

  const handleAddProduct = (product) => {    
    // Extract item_offers_price and item_tax from selected capacity or capacitydetails
    let itemOffersPrice = 0;
    let itemTax = 0;
    let capacity = "";
    let selectedCapacityId = product.selectedCapacity?.id || null;
    
    if (product.selectedCapacity) {
      const cap = product.selectedCapacity;
      itemOffersPrice = cap.item_offers_price || cap.item_selling_price || cap.item_price || 0;
      itemTax = cap.item_tax || 0;
      capacity = cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
      selectedCapacityId = cap.id || selectedCapacityId;
    } else if (product.capacitydetails) {
      const capacityDetails = product.capacitydetails;
      if (typeof capacityDetails === 'object' && !Array.isArray(capacityDetails)) {
        const invIdStr = String(inventoryId);
        const invIdNum = inventoryId;
        let cap = capacityDetails[invIdStr]?.capacities?.[0];
        if (!cap && capacityDetails[invIdNum]) {
          cap = capacityDetails[invIdNum]?.capacities?.[0];
        }
        if (!cap) {
          const firstKey = Object.keys(capacityDetails)[0];
          cap = capacityDetails[firstKey]?.capacities?.[0];
        }
        if (cap) {
          itemOffersPrice = cap.item_offers_price || cap.item_selling_price || cap.item_price || 0;
          itemTax = cap.item_tax || 0;
          capacity = cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
          selectedCapacityId = cap.id || selectedCapacityId;
        }
      } else if (Array.isArray(capacityDetails) && capacityDetails.length > 0) {
        const cap = capacityDetails[0];
        itemOffersPrice = cap.item_offers_price || cap.item_selling_price || cap.item_price || 0;
        itemTax = cap.item_tax || 0;
        capacity = cap.item_capacity || `${cap.capacityValue || ''} ${cap.unit || ''}`.trim();
        selectedCapacityId = cap.id || selectedCapacityId;
      }
    }
    
    // Fallback to direct product fields if capacitydetails not available
    if (!itemOffersPrice) {
      itemOffersPrice = product.item_offers_price || product.item_selling_price || product.item_price || 0;
    }
    if (!itemTax) {
      itemTax = product.item_tax || 0;
    }
    
    // Use capacityInfo from search if available, otherwise use extracted capacity
    if (product.capacityInfo && product.capacityInfo !== '-') {
      capacity = product.capacityInfo;
    } else if (!capacity && product.item_capacity) {
      capacity = product.item_capacity;
    } else if (!capacity && product.capacityValue && product.unit) {
      capacity = `${product.capacityValue} ${product.unit}`.trim();
    }
    
    // For display, use item_offers_price as price
    const price = itemOffersPrice;
    // item_tax is stored as percentage (e.g., 5, 18, etc.)
    const taxPercentage = itemTax; // This is a percentage
    const taxAmount = (price * taxPercentage / 100); // Calculate tax amount from percentage
    const singleItemTotal = parseFloat((price + taxAmount).toFixed(2)); // total = item_offers_price + tax_amount

    // Check if product with same productId and capacity already exists
    const existingProductIndex = products.findIndex((p) => {
      const sameProductId = (p.productId || p.id) === product.id;
      const sameCapacity = 
        (selectedCapacityId && p.selectedCapacityId && p.selectedCapacityId === selectedCapacityId) ||
        (!selectedCapacityId && !p.selectedCapacityId && p.capacity === (capacity || "-"));
      return sameProductId && sameCapacity;
    });

    setBills((prev) => {
      const updatedBills = prev.map((bill, index) => {
        if (index === activeBill) {
          if (existingProductIndex !== -1) {
            // Product with same capacity exists, increment quantity
            const existingProduct = bill.products[existingProductIndex];
            const newQuantity = existingProduct.quantity + 1;
            const subtotal = itemOffersPrice * newQuantity;
            // item_tax is a percentage, calculate tax amount
            const taxPercentage = itemTax; // This is a percentage
            const taxTotal = (itemOffersPrice * taxPercentage / 100) * newQuantity;
            const newTotal = parseFloat((subtotal + taxTotal).toFixed(2));
            
            const updatedProducts = bill.products.map((p, idx) => {
              if (idx === existingProductIndex) {
                return {
                  ...p,
                  quantity: newQuantity,
                  total: newTotal,
                };
              }
              return p;
            });
            
            return {
              ...bill,
              products: updatedProducts,
            };
          } else {
            const productId = Date.now() + Math.random();
            const newProduct = {
              id: productId,
              code: product.sku_code || product.code || "",
              name: product.item_name || product.name || "",
              price: price, 
              quantity: 1,
              discount: 0,
              gst: taxAmount, // Store tax amount for backward compatibility
              total: singleItemTotal,
              productId: product.id, 
              capacity: capacity || "-", 
              itemOffersPrice: itemOffersPrice, 
              itemTax: taxPercentage, // Store tax percentage (not amount)
              selectedCapacityId: selectedCapacityId, 
              selectedCapacityInfo: product.capacityInfo || capacity || null,
              // Preserve the exact chosen capacity object so checkout can place the correct variant
              selectedCapacity: product.selectedCapacity || null,
            };
            
            return {
              ...bill,
              products: [...bill.products, newProduct],
            };
          }
        }
        return bill;
      });
      
      // Sync with backend using updated products
      const activeBillProducts = updatedBills[activeBill]?.products || [];
      syncQuantityWithBackend(activeBillProducts);
      
      return updatedBills;
    });

    // Clear search
    setSearchProduct("");
    setSearchResults([]);
    setShowSearchResults(false);
    setHighlightedIndex(-1);
  };

  const handleSearchKeyDown = (e) => {
    if (!showSearchResults || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleAddProduct(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSearchResults(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClickAway = () => {
    setShowSearchResults(false);
    setHighlightedIndex(-1);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const calculateSubtotal = () => {
    // sub_total = sum of (item_offers_price * quantity)
    return products.reduce((sum, product) => {
      const itemOffersPrice = product.itemOffersPrice || product.price;
      const subtotal = itemOffersPrice * product.quantity;
      return sum + subtotal;
    }, 0);
  };

  const calculateGST = () => {
    return products.reduce((sum, product) => {
      const itemOffersPrice = product.itemOffersPrice || product.price;
      const taxPercentage = product.itemTax || product.gst || 0; // This is a percentage
      const taxAmount = (itemOffersPrice * taxPercentage / 100) * product.quantity;
      return sum + taxAmount;
    }, 0);
  };
  useEffect(() => {
    const t = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [activeBill]);
  
  const calculateTaxPercentage = () => {
    let weightedTaxSum = 0;
    let totalPriceQty = 0;
    
    products.forEach((product) => {
      const itemOffersPrice = product.itemOffersPrice || product.price;
      const taxPercentage = product.itemTax || product.gst || 0;
      const priceQty = itemOffersPrice * product.quantity;
      
      weightedTaxSum += priceQty * taxPercentage;
      totalPriceQty += priceQty;
    });
    
    return totalPriceQty > 0 
      ? Number((weightedTaxSum / totalPriceQty).toFixed(2))
      : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGST();
  };

  const calculateBalance = () => {
    const total = calculateTotal();
    const cash = parseFloat(cashAmount) || 0;
    return cash - total;
  };

  const handleBillTabChange = (event, newValue) => {
    setActiveBill(newValue);
  };

  const handleCheckoutAndPrint = async () => {
    if (isCheckingOut) return;
  
    setIsCheckingOut(true);
  
    try {
      if (!user || !inventoryId) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "User or inventory ID not available",
          life: 3000,
        });
        return;
      }
  
      if (products.length === 0) {
        toast.current?.show({
          severity: "warn",
          summary: "Warning",
          detail: "Please add products to the bill before checkout",
          life: 3000,
        });
        return;
      }
  
      const trimmedCustomerName = bills[activeBill]?.customerName.trim();
      const trimmedMobile = bills[activeBill]?.mobileNumber.trim();
  
      // 🔹 Mobile validation
      if (trimmedMobile) {
        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(trimmedMobile)) {
          setBills((prev) =>
            prev.map((bill, i) =>
              i === activeBill
                ? {
                    ...bill,
                    mobileNumberError: "Please enter a valid 10-digit mobile number",
                  }
                : bill
            )
          );
  
          toast.current?.show({
            severity: "error",
            summary: "Validation Error",
            detail: "Please enter a valid 10-digit mobile number",
            life: 3000,
          });
  
          return;
        }
      }
  
      // clear mobile error
      setBills((prev) =>
        prev.map((bill, i) =>
          i === activeBill ? { ...bill, mobileNumberError: "" } : bill
        )
      );
  
      let customerUserId = user;
  
      // 🔹 Get or create user by mobile
      if (trimmedMobile) {
        try {
          const userResponse = await CustomAxios.post(
            "get_or_create_user_by_mobile/",
            {
              mobileno: trimmedMobile,
              first_name: trimmedCustomerName || "",
              last_name: "",
            }
          );
  
          if (userResponse?.data?.user_id) {
            customerUserId = userResponse.data.user_id;
          } else {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to get or create user. Please try again.",
              life: 3000,
            });
            return;
          }
        } catch (userError) {
          console.error("Error getting/creating user:", userError);
  
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              "Failed to get or create user. Please check mobile number and try again.",
            life: 3000,
          });
  
          return;
        }
      }
  
      // 🔹 Capacity parser fallback
      const parseCapacityString = (value) => {
        const raw = String(value || "").trim();
        const m = raw.match(/^([\d.\/]+)\s*([a-zA-Z]+)$/);
  
        if (!m)
          return {
            capacityValue: "1",
            unit: "pieces",
            item_capacity: raw || "1 pieces",
          };
  
        return {
          capacityValue: m[1] || "1",
          unit: (m[2] || "pieces").toLowerCase(),
          item_capacity: raw,
        };
      };
  
      // 🔹 Build products payload with EXACT selected capacity
      const productsForOrder = products.map((product) => {
        const product_id = product.productId || product.id;
        const quantity = Number(product.quantity) || 1;
  
        if (product.selectedCapacity && typeof product.selectedCapacity === "object") {
          const cap = product.selectedCapacity;
  
          return {
            product_id,
            quantity,
            capacity: [
              {
                ...cap,
                product_id,
                inventory_id: cap.inventory_id || inventoryId,
                item_quantity: String(quantity),
              },
            ],
          };
        }
  
        // fallback minimal capacity
        const parsed = parseCapacityString(product.capacity);
  
        return {
          product_id,
          quantity,
          capacity: [
            {
              product_id,
              inventory_id: inventoryId,
              unit: parsed.unit || "pieces",
              capacity: parsed.item_capacity || "1 pieces",
              capacityValue: parsed.capacityValue || "1",
              item_capacity: parsed.item_capacity || "1 pieces",
              item_price: product.itemOffersPrice || product.price || 0,
              item_tax: product.itemTax || product.gst || 0,
              item_quantity: String(quantity),
              item_available: true,
              extraPercentage: 0,
              item_offers_price: product.itemOffersPrice || product.price || 0,
              item_selling_price: product.itemOffersPrice || product.price || 0,
              item_special_offer: 0,
            },
          ],
        };
      });

      const subtotal = calculateSubtotal();
      const gst = calculateGST();
      const total = calculateTotal();

      // Sync cart on server using the exact capacities we're about to place
      await CustomAxios.post("kiosk_order_creation/", {
        products: productsForOrder,
        inventory_id: inventoryId,
        user: customerUserId,
      });

      const payload = {
        products: productsForOrder,
        total_Amount: parseFloat(total.toFixed(2)),
        amount: parseFloat(subtotal.toFixed(2)),
        tax: parseFloat(gst.toFixed(2)),
        user: customerUserId,
        inventory_id: inventoryId,
        deliveryMode: 3,
        payment_mode: paymentMethod.toLowerCase(),
        order_type: "walkin",
        customer_name: trimmedCustomerName || null,
        mobileno: trimmedMobile || null,
      };
  
      const response = await CustomAxios.post("kiosk_order_placement/", payload);
  
      // 🔹 SUCCESS
      if (response?.data?.message === "Successfully order created") {
        if (response.data.order_id) {
          setInvoiceId(response.data.order_id);
  
          // 🔹 Print PDF
          try {
            const billingResponse = await CustomAxios.post(
              "place_bill_machine/",
              {
                user_id: customerUserId,
                order_id: response.data.order_id,
                payload,
              },
              { responseType: "blob" }
            );
  
            const blob = new Blob([billingResponse.data], { type: "application/pdf" });
            const pdfUrl = URL.createObjectURL(blob);
  
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = pdfUrl;
            document.body.appendChild(iframe);
  
            iframe.onload = () => {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
              setTimeout(() => document.body.removeChild(iframe), 15000);
            };
          } catch (printError) {
            console.error("Error generating PDF:", printError);
  
            toast.current?.show({
              severity: "warn",
              summary: "Warning",
              detail:
                "Order placed successfully but failed to generate PDF. Please try again.",
              life: 3000,
            });
          }
        }
  
        // 🔹 Clear bill
        setBills((prev) =>
          prev.map((bill, i) =>
            i === activeBill
              ? { ...bill, products: [], customerName: "", mobileNumber: "", mobileNumberError: "" }
              : bill
          )
        );
  
        setInvoiceId("XXXXXXX");
        setCashAmount("");
  
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Order placed successfully!",
          life: 3000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to place order. Please try again.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error during checkout:", error);
  
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to place order. Please try again.",
        life: 3000,
      });
    } finally {
      setIsCheckingOut(false);
    }
  };
  

  useEffect(() => {
    const handleGlobalCtrlEnter = (e) => {
      if (!(e.ctrlKey && e.key === "Enter")) return;
  
      e.preventDefault();
  
      handleCheckoutAndPrint();
    };
  
    window.addEventListener("keydown", handleGlobalCtrlEnter);
    return () => window.removeEventListener("keydown", handleGlobalCtrlEnter);
  }, [handleCheckoutAndPrint]);

  const subtotal = calculateSubtotal();
  const gst = calculateGST();
  const taxPercentage = calculateTaxPercentage();
  const total = calculateTotal();
  const balance = calculateBalance();
  
  const gstLabel = taxPercentage !== null && taxPercentage > 0
    ? `GST`
    : "GST";

  return (
    <Box
    ref={pageRef}
    onKeyDown={handleKeyDownOnlyInputs}
    sx={{ display: "flex", gap: 2, padding: 2, height: "calc(100vh - 100px)" }}
  >
      <Toast ref={toast} />
      {/* Left Section - Product Listing and Bill Management */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: "20px",
              fontWeight: "700",
              lineHeight: "28px",
              color: "text.primary",
            }}
          >
            Point of Sale
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: "400",
              color: "text.secondary",
              marginTop: "4px",
            }}
          >
            Point of Sale &gt; Create and manage customer orders
          </Typography>
        </Box>

        {/* Bill Tabs */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tabs
            value={activeBill}
            onChange={handleBillTabChange}
            sx={{
              "& .MuiTab-root": {
                minWidth: "80px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: "#FF6B35",
                fontWeight: "600",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#FF6B35",
              },
            }}
          >
            {bills.map((bill, index) => (
              <Tab
                key={bill.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography component="span">{bill.name}</Typography>
                    {bills.length > 1 && (
                      <IconButton
                        tabIndex={-1}
                        size="small"
                        onClick={(e) => handleCloseBill(e, index)}
                        sx={{
                          padding: "2px",
                          marginLeft: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "14px" }} />
                      </IconButton>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Button
            tabIndex={-1}
            variant="outlined"
            onClick={handleNewBill}
            sx={{
              marginLeft: "auto",
              textTransform: "none",
              borderColor: "#FF6B35",
              color: "#FF6B35",
              "&:hover": {
                borderColor: "#FF6B35",
                backgroundColor: "rgba(255, 107, 53, 0.1)",
              },
            }}
          >
            + New Bill
          </Button>
        </Box>

        {/* Product Search */}
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box sx={{ position: "relative" }}>
        <TextField
              inputRef={searchInputRef}
          placeholder="Search Products"
          value={searchProduct}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
            },
          }}
        />
            {showSearchResults && searchResults.length > 0 && (
              <Popper
                open={showSearchResults}
                anchorEl={searchInputRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: searchInputRef.current?.offsetWidth }}
              >
                <Paper
                  sx={{
                    width: "100%",
                    maxHeight: "300px",
                    overflow: "auto",
                    mt: 0.5,
                    boxShadow: 3,
                  }}
                >
                  <List dense>
                    {searchResults.map((product, index) => (
                      <ListItem key={product.searchId || product.id || index} disablePadding>
                        <ListItemButton
                          onClick={() => handleAddProduct(product)}
                          selected={index === highlightedIndex}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(255, 107, 53, 0.1)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "rgba(255, 107, 53, 0.2)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 107, 53, 0.3)",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {product.item_name || product.name || "Unknown Product"}
                                </Typography>
                                {product.capacityInfo && (
                                  <Typography variant="caption" sx={{ color: "#FF6B35", fontWeight: 600 }}>
                                    {product.capacityInfo}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              product.sku_code
                                ? `Code: ${product.sku_code}`
                                : product.code
                                ? `Code: ${product.code}`
                                : ""
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Popper>
            )}
            {showSearchResults && searchResults.length === 0 && !searchLoading && searchProduct.trim().length >= 3 && (
              <Popper
                open={showSearchResults}
                anchorEl={searchInputRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: searchInputRef.current?.offsetWidth }}
              >
                <Paper
                  sx={{
                    width: "100%",
                    mt: 0.5,
                    boxShadow: 3,
                    p: 2,
                  }}
                >
                  <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
                    No products found
                  </Typography>
                </Paper>
              </Popper>
            )}
          </Box>
        </ClickAwayListener>

        {/* Product Table */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "white",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>S#</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Product Code</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Price</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Discount</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>GST</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Total</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Capacity</TableCell>
                <TableCell sx={{ fontWeight: "600", fontSize: "14px" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length > 0 ? (
                products.map((product, index) => (
                <TableRow key={product.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{product.code}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>₹{formatCurrency(product.price)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        tabIndex={-1}
                        size="small"
                        onClick={() => handleQuantityChange(product.id, -1)}
                        sx={{
                          border: "1px solid #ddd",
                          width: "28px",
                          height: "28px",
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ minWidth: "30px", textAlign: "center" }}>
                        {product.quantity}
                      </Typography>
                      <IconButton
                        tabIndex={-1}
                        size="small"
                        onClick={() => handleQuantityChange(product.id, 1)}
                        sx={{
                          border: "1px solid #ddd",
                          width: "28px",
                          height: "28px",
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{product.discount || "-"}</TableCell>
                  <TableCell>
                    ₹
                    {formatCurrency(
                      ((product.itemOffersPrice || product.price) * (product.itemTax || product.gst || 0) / 100) * product.quantity
                    )}
                  </TableCell>
                  <TableCell>₹{formatCurrency(product.total)}</TableCell>
                    <TableCell>{product.capacity || "-"}</TableCell>
                  <TableCell>
                    <IconButton
                    tabIndex={-1}
                      size="small"
                      onClick={() => handleRemoveProduct(product.id)}
                      sx={{ color: "#f44336" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ padding: 4 }}>
                    <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
                      No products added yet. Start adding products to this bill.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Right Section - Invoice Details, Payment, and Summary */}
      <Box
        sx={{
          width: "400px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "white",
          padding: 2,
          borderRadius: "8px",
        }}
      >
        {/* Invoice Search */}
        <TextField
          placeholder="Search Invoice / Mobile Number"
          value={searchInvoice}
          onChange={(e) => setSearchInvoice(e.target.value)}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Invoice Information */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "12px", color: "text.secondary", marginBottom: "4px" }}>
              Invoice ID
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#FF6B35",
              }}
            >
              {invoiceId}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "12px", color: "text.secondary", marginBottom: "4px" }}>
              Date
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              {invoiceDate}
            </Typography>
          </Box>

          <TextField
            label="Customer Name (Optional)"
            value={activeCustomerName}
            onChange={(e) => {
              const value = e.target.value;
              setBills(prev =>
                prev.map((bill, i) =>
                  i === activeBill
                    ? { ...bill, customerName: value }
                    : bill
                )
              );
            }}
            fullWidth
            size="small"
            sx={{ marginBottom: 2 }}
          />

          <TextField
            label="Mobile Number (Optional)"
            value={activeMobile}
onChange={(e) => {
  const value = e.target.value.replace(/\D/g, "");
  if (value.length <= 10) {
    setBills(prev =>
      prev.map((bill, i) =>
        i === activeBill
          ? { ...bill, mobileNumber: value, mobileNumberError: "" }
          : bill
      )
    );
  }
}}
error={!!activeMobileError}
helperText={activeMobileError}

            fullWidth
            size="small"
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
            }}
          />
        </Box>

        {/* Billing Summary */}
        <Box
          sx={{
            borderTop: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
            paddingY: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
              Subtotal
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              ₹{subtotal.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
              {gstLabel}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              ₹{gst.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: "600" }}>Total</Typography>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#FF6B35",
              }}
            >
              ₹{total.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Payment Method */}
        <Box>
          <Typography sx={{ fontSize: "14px", fontWeight: "600", marginBottom: 1 }}>
            Payment Method
          </Typography>
          <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
            {["CASH", "CARD", "UPI"].map((method) => (
              <Button
                tabIndex={-1}
                key={method}
                variant={paymentMethod === method ? "contained" : "outlined"}
                onClick={() => setPaymentMethod(method)}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  backgroundColor:
                    paymentMethod === method ? "#FF6B35" : "transparent",
                  color: paymentMethod === method ? "white" : "#FF6B35",
                  borderColor: "#FF6B35",
                  "&:hover": {
                    backgroundColor:
                      paymentMethod === method ? "#FF6B35" : "rgba(255, 107, 53, 0.1)",
                  },
                }}
              >
                {method}
              </Button>
            ))}
          </Box>
          {paymentMethod === "CASH" && (
            <TextField
              label="₹ Enter the Cash amount"
              value={cashAmount}
              onChange={(e) => setCashAmount(e.target.value)}
              fullWidth
              size="small"
              type="number"
            />
          )}
        </Box>

        {/* Payment Calculation */}
        {paymentMethod === "CASH" && cashAmount && (
          <Box
            sx={{
              backgroundColor: "#F5F5F5",
              padding: 1.5,
              borderRadius: "4px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                Bill amount
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                ₹{total.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: "600", color: "#1976d2" }}>
                Balance to Pay Customer
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1976d2",
                }}
              >
                ₹{balance >= 0 ? balance.toFixed(2) : "0.00"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Status Buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            tabIndex={-1}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#4CAF50",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#45a049",
              },
            }}
          >
            BILL PAID
          </Button>
          <Button
            tabIndex={-1}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#f44336",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#da190b",
              },
            }}
          >
            CANCELLED
          </Button>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, marginTop: "auto" }}>
          <Button
            tabIndex={-1}
            variant="outlined"
            fullWidth
            onClick={handleClearBill}
            sx={{
              borderColor: "#FF6B35",
              color: "#FF6B35",
              textTransform: "none",
              "&:hover": {
                borderColor: "#FF6B35",
                backgroundColor: "rgba(255, 107, 53, 0.1)",
              },
            }}
          >
            Clear
          </Button>
          <Tooltip title="Shortcut: Ctrl + Enter" arrow placement="top">
            <span style={{ width: "100%" }}>
              <Button
                tabIndex={-1}
                variant="contained"
                fullWidth
                onClick={handleCheckoutAndPrint}
                sx={{
                  backgroundColor: "#1976d2",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                Checkout & Print
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
